import { ReactNode, useState } from "react";
import { Linking, View, Pressable, Platform } from "react-native";
import { Feather } from "@expo/vector-icons";
import { Text } from "react-native-paper";

import { usePushNotificationsPermissionStatus, useSetBadgeAcknowleged } from "./hooks";
import { Button, ScreenContainer, PopOver } from "../../components";
import { ScreenProps } from "./types";
import { NotificationPermission } from "../../hooks/useCurrentUser/types";
import { Header } from "../../components/HeaderBackButton";

function Bullet({ children }: { children: ReactNode }) {
  return (
    <View className="flex-row">
      <Text className="text-regular-normal font-400 mx-2">•</Text>
      <Text className="text-regular-normal font-400 text-ink-dark flex-shrink">{children}</Text>
    </View>
  );
}

export function EmailChangeNotice({
  visible,
  onDismiss,
  onContinue,
}: {
  visible: boolean;
  onDismiss(): void;
  onContinue(): void;
}) {
  return (
    <PopOver {...{ visible, onDismiss }} testID="email-change-notice-popover">
      <Text className="text-regular-normal font-700 mb-2">Please note, once verified, your new email address will be:</Text>
      <Bullet>The primary contact method for PGA communications.</Bullet>
      <Bullet>
        Your new username when logging into PGA Member digital experiences such as the Member App, PGA.org and PGA Coach.
      </Bullet>

      <View className="flex-row justify-end mt-6">
        <Button mode="text" size="small" className="mr-4" onPress={onDismiss}>
          Cancel
        </Button>
        <Button
          mode="secondary"
          size="small"
          onPress={() => {
            onDismiss();
            onContinue();
          }}
        >
          Continue
        </Button>
      </View>
    </PopOver>
  );
}

function PushNotifications() {
  function openNotificationSettings() {
    Linking.openSettings();
  }

  const pushNotificationsEnabled = usePushNotificationsPermissionStatus();
  useSetBadgeAcknowleged(pushNotificationsEnabled);

  const notificationStatusWord =
    pushNotificationsEnabled === NotificationPermission.GRANTED ? (
      <Text className="text-regular-tight font-500">ON</Text>
    ) : (
      <Text className="text-red text-regular-tight font-500">OFF</Text>
    );

  return (
    <Pressable
      className="active:bg-sky-light py-2 px-6 border-b border-sky-lighter flex-row items-center"
      onPress={openNotificationSettings}
    >
      <Feather name="bell" size={20} />
      <View className="ml-4">
        <Text className="text-regular-tight">Push Notifications</Text>

        <Text className="mt-2 text-small-tight text-ink-lighter">
          Modify notification settings <Text className="text-primary underline">here</Text>
        </Text>
      </View>
      <View className="flex-row justify-end flex-1">{notificationStatusWord}</View>
    </Pressable>
  );
}

export const navigationTitle = "Account Settings";

export default function SettingsScreen({ navigation }: ScreenProps<"AccountSettings">) {
  const [showEmailChangeNotice, setShowEmailChangeNotice] = useState(false);
  return (
    <ScreenContainer navigationTitle={navigationTitle}>
      <Header goBack={navigation.goBack}>{navigationTitle}</Header>
      <Pressable
        className="active:bg-sky-light py-5 px-6 mt-2 border-b border-sky-lighter flex-row items-center"
        onPress={() => {
          setShowEmailChangeNotice(true);
        }}
      >
        <Feather name="mail" size={20} />
        <Text className="ml-4 text-regular-tight">Change Login/Contact Email</Text>
      </Pressable>

      {Platform.OS !== "web" && <PushNotifications />}

      <EmailChangeNotice
        visible={showEmailChangeNotice}
        onContinue={() => {
          navigation.navigate("EmailChange");
        }}
        onDismiss={() => {
          setShowEmailChangeNotice(false);
        }}
      />
    </ScreenContainer>
  );
}
